import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Card,
  Checkbox,
  Dropdown,
  Icon,
  Input,
  Label,
  Loader,
  Message,
  Modal,
  Popup,
  Segment,
  Statistic,
  Table,
} from 'semantic-ui-react';
import {
  isMediaPriceV111,
  mediaPriceLabelV111,
  mediaPriceScopeV111,
  mediaPriceUnitV111,
} from './mediaPricePresentationV111';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { organizationUsageTextV822 } from '../CustomerVertexLogV811/organizationUsageV822';
import {
  API,
  copy,
  isAdmin,
  isRoot,
  showSuccess,
  showWarning,
} from '../../helpers';
import { SourcePriceSelectorV107 } from '../CommercialV106/PriceOfferSelectorV106';
import { MODEL_CATEGORY_META_V814 } from './modelPresentationV814';
import {
  buildModelPresentationV10,
  matchesCatalogFiltersV10,
  providerFacetsV10,
  MODEL_BRANDS_V10,
} from './modelBrandsV10';
import ModelBrandIconV10 from './ModelBrandIconV10';
import './modelCatalogV814.css';
import './modelCatalogV10.css';
import {
  catalogSummaryV1020,
  mergeCatalogStageV1020,
  catalogStageTextV1020,
} from './catalogStagesV1020';
import './modelDiscoveryV109.css';
import { modelDiscoveryTextV109 } from './modelDiscoveryV109';
import { modelPriceGapTextV1018 } from './modelPriceGapsV1018';
import './modelPriceGapsV1018.css';
import { ModelContactV109, OtherModelCardV109 } from './OtherModelsV109';
import {
  catalogHealthScoreV101,
  catalogHealthTextV101,
} from './catalogHealthV101';
import {
  catalogPriceOptionsV108,
  priceSummaryLabelV108,
  selectCardPricesV108,
} from './modelPriceSummaryV108';
import {
  captureCatalogAnchorV1015,
  restoreCatalogAnchorV1015,
  stableCatalogOrderV1015,
} from './catalogRefreshV1015';
import {
  MODEL_SORT_KEY_V1021,
  catalogSortPreferenceV1021,
  compareCatalogNewestV1021,
  compareCatalogDateV113,
  compareCatalogNameV113,
  compareCatalogCapabilityV113,
  compareCatalogProviderV113,
  catalogIntelligenceV113,
  catalogDateV113,
} from './catalogRecencyV1021';
import {
  MODEL_PRECISION_KEY_V113,
  catalogPrecisionPreferenceV113,
  catalogDecimalV113,
  catalogPriceV113,
  catalogDisplayTextV113,
} from './catalogDisplayV113';
import './catalogDisplayV113.css';
import RegisteredChannelsV1021 from './RegisteredChannelsV1021';
import ModelSiteOriginsV1024 from './ModelSiteOriginsV1024';
import {
  MODEL_COMPACT_KEY_V113,
  catalogCompactPreferenceV113,
  catalogMetricSortV113,
  catalogPaginationTextV113,
  CatalogPaginationV113,
} from './catalogPaginationV113';
import './catalogPaginationV113.css';
import OfficialModelsV115 from './OfficialModelsV115';
import { OfficialSealV115, OfficialShieldV115 } from './OfficialSealV115';

const MODEL_PERIOD_KEY = 'alfredapi.model.statistics_period.v7';
const MODEL_SORT_KEY = MODEL_SORT_KEY_V1021;
const VALID_PERIODS = ['day', 'month', 'year'];
const CATEGORY_FILTERS_V814 = [
  { key: 'all', icon: 'th large' },
  { key: 'language', icon: 'comments outline' },
  { key: 'image', icon: 'image outline' },
  { key: 'video', icon: 'video' },
  { key: 'audio', icon: 'volume up' },
];

const Model = () => {
  const { t, i18n } = useTranslation();
  const healthText = catalogHealthTextV101(i18n?.language);
  const paginationText = catalogPaginationTextV113(i18n?.language);
  const displayText = catalogDisplayTextV113(i18n?.language);
  const [fullPrecision, setFullPrecision] = useState(() =>
    catalogPrecisionPreferenceV113(localStorage)
  );
  const admin = isAdmin();
  const root = isRoot();
  const discoveryText = modelDiscoveryTextV109(i18n?.language);
  const priceGapText = modelPriceGapTextV1018(i18n?.language);
  const [priceGapMode, setPriceGapMode] = useState(false);
  const priceGaps = root && priceGapMode;
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const discovery = !admin && discoveryMode;
  const [contactModel, setContactModel] = useState(null);
  const [models, setModels] = useState([]);
  const [officialOnly, setOfficialOnly] = useState(false);
  const [metadata, setMetadata] = useState({});
  const [compactMode, setCompactMode] = useState(() =>
    catalogCompactPreferenceV113(localStorage)
  );
  const [pageState, setPageState] = useState({ scope: '', page: 1 });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const loadGeneration = useRef(0);
  const inFlight = useRef(null);
  const requestAbortV1016 = useRef(null);
  const hasCatalog = useRef(false);
  const catalogElement = useRef(null);
  const pendingAnchor = useRef(null);
  const catalogOrder = useRef({ scope: '', names: [] });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('');
  const [providerFilters, setProviderFilters] = useState([]);
  const [infoModel, setInfoModel] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [period, setPeriod] = useState(() => {
    const stored = localStorage.getItem(MODEL_PERIOD_KEY);
    return root && VALID_PERIODS.includes(stored) ? stored : 'day';
  });
  const [sortMode, setSortMode] = useState(() =>
    catalogSortPreferenceV1021(localStorage)
  );
  const effectiveSort =
    (discovery || priceGaps || compactMode) &&
    !['newest', 'oldest', 'name', 'provider', 'capability'].includes(sortMode)
      ? 'newest'
      : sortMode;
  const pageScope = JSON.stringify([
    compactMode, search, categoryFilter, groupFilter, providerFilters,
    effectiveSort, discovery, priceGaps, officialOnly,
  ]);
  const page = pageState.scope === pageScope ? pageState.page : 1;
  useEffect(() => {
    setPageState((current) =>
      current.scope === pageScope ? current : { scope: pageScope, page: 1 }
    );
  }, [pageScope]);
  const pageParameters = JSON.stringify({
    paged_v113: 1, page, compact: compactMode, search,
    category: categoryFilter, group: groupFilter,
    providers: providerFilters.join(','), sort: effectiveSort,
    official_only: officialOnly,
  });

  const loadModels = useCallback(
    async (background = false) => {
      if (inFlight.current !== null) return;
      const quiet = background === true && hasCatalog.current;
      const request = ++loadGeneration.current;
      inFlight.current = request;
      const abort = new AbortController();
      requestAbortV1016.current = abort;
      if (!quiet) {
        catalogOrder.current = { scope: '', names: [] };
        setLoading(true);
        setLoadFailed(false);
      }
      try {
        const query = root ? `?period=${period}` : '';
        const response = await API.get(
          priceGaps
            ? '/api/billing/v8/admin/model-price-catalog'
            : discovery
            ? '/api/user/other_models'
            : `/api/user/model_catalog${query}`,
          {
            timeout: 30000,
            ...(!priceGaps && !discovery
              ? { params: { ...JSON.parse(pageParameters), view: 'summary' } }
              : {}),
            signal: abort.signal,
            // Keep the shared 401 expiry flow; inspect other client denials here.
            validateStatus: (status) =>
              status >= 200 && status < 500 && status !== 401 && status !== 429,
          }
        );
        const payload = response && response.data;
        if (request !== loadGeneration.current) return;
        if (
          response?.status >= 400 ||
          !payload ||
          !payload.success ||
          !payload.data ||
          !Array.isArray(payload.data.models)
        ) {
          const error = new Error('Invalid model catalog response');
          error.denied =
            payload?.success === false ||
            (response?.status >= 400 && response.status < 500);
          throw error;
        }
        if (quiet) {
          pendingAnchor.current = captureCatalogAnchorV1015(
            catalogElement.current
          );
        }
        const staged = payload.data.catalog_stage_v1020 === 'summary';
        setModels(
          staged
            ? catalogSummaryV1020({ ...payload.data, compact_v113: compactMode })
            : payload.data.models
        );
        setMetadata({ ...payload.data, catalog_load_v113: request });
        hasCatalog.current = true;
        setLoadFailed(false);
        setRefreshFailed(false);
        if (staged && payload.data.models.length > 0) {
          setLoading(false);
          // The default SQLite pool is shared by pricing and log statistics.
          // Finish prices first so a slow statistics read cannot starve them.
          for (const stage of compactMode ? ['prices'] : ['prices', 'statistics']) {
            if (abort.signal.aborted || request !== loadGeneration.current)
              break;
            try {
              const detail = await API.get(`/api/user/model_catalog${query}`, {
                timeout: 30000,
                params: {
                  ...JSON.parse(pageParameters), view: stage,
                  page_revision_v113: payload.data.pagination_v113?.revision,
                },
                signal: abort.signal,
                validateStatus: (status) =>
                  status >= 200 &&
                  status < 500 &&
                  status !== 401 &&
                  status !== 429,
              });
              if (abort.signal.aborted || request !== loadGeneration.current)
                return;
              const body = detail?.data;
              if (
                [401, 403].includes(detail?.status) ||
                body?.catalog_access_denied_v1020
              ) {
                const error = new Error('Catalog access denied');
                error.denied = true;
                throw error;
              }
              if (
                !body?.success ||
                !Array.isArray(body.data?.models) ||
                body.data.catalog_stage_v1020 !== stage
              ) {
                throw new Error('Catalog detail unavailable');
              }
              pendingAnchor.current = captureCatalogAnchorV1015(
                catalogElement.current
              );
              setModels((current) =>
                mergeCatalogStageV1020(current, body.data, stage)
              );
              if (stage === 'statistics') {
                if (!quiet) catalogOrder.current = { scope: '', names: [] };
                setMetadata((current) => ({
                  ...current,
                  period: body.data.period,
                  period_start: body.data.period_start,
                  period_end: body.data.period_end,
                  bucket_unit: body.data.bucket_unit,
                  generated_at: body.data.generated_at,
                  platform_total_requests: body.data.platform_total_requests,
                  statistics_ready_v1020: true,
                }));
              }
            } catch (error) {
              if (abort.signal.aborted || request !== loadGeneration.current)
                return;
              if (error.denied || [401, 403].includes(error.response?.status)) {
                abort.abort();
                hasCatalog.current = false;
                setModels([]);
                setMetadata({});
                setInfoModel(null);
                setSelectedModel(null);
                setLoadFailed(true);
              } else {
                setModels((current) =>
                  current.map((item) => ({
                    ...item,
                    [`${stage}_state_v1020`]: 'error',
                  }))
                );
              }
            }
          }
        }
      } catch (error) {
        if (abort.signal.aborted) return;
        if (request !== loadGeneration.current) return;
        const denied =
          error.denied || [401, 403].includes(error.response?.status);
        if (quiet && !denied) {
          pendingAnchor.current = captureCatalogAnchorV1015(
            catalogElement.current
          );
          setRefreshFailed(true);
        } else {
          hasCatalog.current = false;
          pendingAnchor.current = null;
          setModels([]);
          setMetadata({});
          setInfoModel(null);
          setSelectedModel(null);
          setContactModel(null);
          setLoadFailed(true);
          setRefreshFailed(false);
        }
      } finally {
        if (request === loadGeneration.current) {
          inFlight.current = null;
          requestAbortV1016.current = null;
          setLoading(false);
        }
      }
    },
    [period, root, discovery, priceGaps, compactMode, pageParameters]
  );

  useEffect(() => {
    hasCatalog.current = false;
    inFlight.current = null;
    pendingAnchor.current = null;
    setRefreshFailed(false);
    loadModels();
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') loadModels(true);
    }, 60000);
    return () => {
      window.clearInterval(timer);
      loadGeneration.current += 1;
      requestAbortV1016.current?.abort();
    };
  }, [loadModels]);

  const changePeriod = (value) => {
    if (!root) return;
    setPeriod(value);
    localStorage.setItem(MODEL_PERIOD_KEY, value);
    setSelectedModel(null);
  };

  const changeSort = (value) => {
    setSortMode(value);
    localStorage.setItem(MODEL_SORT_KEY, value);
  };

  const changeCompact = (_, { checked }) => {
    setCompactMode(checked);
    setSelectedModel(null);
    try {
      localStorage.setItem(MODEL_COMPACT_KEY_V113, String(checked));
    } catch {
      // The preference still applies for this visit when storage is unavailable.
    }
  };

  const changePage = (next) => {
    setPageState({ scope: pageScope, page: next });
    setInfoModel(null);
    setSelectedModel(null);
    catalogElement.current?.scrollIntoView?.({ block: 'start' });
  };

  const copyModelId = async (modelId) => {
    if (await copy(modelId)) {
      showSuccess(t('models.copy_success', { model: modelId }));
    } else {
      showWarning(t('models.copy_failed'));
    }
  };

  const presentedModels = useMemo(
    () =>
      models.map((item) => ({
        ...item,
        presentation: buildModelPresentationV10(item),
      })),
    [models]
  );

  const groupOptions = useMemo(() => {
    const groupSet = new Set(
      (metadata.pagination_v113?.groups || (Array.isArray(metadata.groups) ? metadata.groups : [])).filter(Boolean)
    );
    presentedModels.forEach((item) => {
      (Array.isArray(item.groups) ? item.groups : []).forEach((group) =>
        groupSet.add(group)
      );
    });
    return [
      { key: 'all', value: '', text: t('models.catalog.group_all') },
      ...[...groupSet]
        .sort((left, right) => left.localeCompare(right))
        .map((group) => ({ key: group, value: group, text: group })),
    ];
  }, [metadata.groups, metadata.pagination_v113, presentedModels, t]);

  useEffect(() => {
    if (!loading &&
      groupFilter &&
      !groupOptions.some((option) => option.value === groupFilter)
    ) {
      setGroupFilter('');
    }
  }, [groupFilter, groupOptions, loading]);

  const providerFacets = useMemo(
    () =>
      metadata.pagination_v113?.providers
      ? Object.entries(MODEL_BRANDS_V10)
          .filter(([key]) => key in metadata.pagination_v113.providers)
          .map(([key, brand]) => ({ key, brand, count: metadata.pagination_v113.providers[key] }))
      : providerFacetsV10(presentedModels, {
        search,
        category: categoryFilter,
        group: groupFilter,
      }),
    [presentedModels, search, categoryFilter, groupFilter, metadata.pagination_v113]
  );
  const facetTotal = providerFacets.reduce(
    (total, facet) => total + facet.count,
    0
  );
  const hasFilters = Boolean(
    search || categoryFilter !== 'all' || groupFilter || providerFilters.length || officialOnly
  );
  const resetFilters = () => {
    setSearch('');
    setCategoryFilter('all');
    setGroupFilter('');
    setProviderFilters([]);
    setOfficialOnly(false);
  };
  const switchCatalog = () => {
    loadGeneration.current += 1;
    setModels([]);
    setMetadata({});
    setLoading(true);
    setLoadFailed(false);
    setInfoModel(null);
    setSelectedModel(null);
    setContactModel(null);
    resetFilters();
    setDiscoveryMode(!discovery);
  };
  const switchPriceGaps = (_, { checked }) => {
    loadGeneration.current += 1;
    requestAbortV1016.current?.abort();
    setModels([]);
    setMetadata({});
    setLoading(true);
    setLoadFailed(false);
    setInfoModel(null);
    setSelectedModel(null);
    resetFilters();
    setPriceGapMode(checked);
  };
  const toggleProvider = (key) =>
    setProviderFilters((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key]
    );
  useEffect(() => {
    if (loading || loadFailed) return;
    const available = new Set(providerFacets.map((facet) => facet.key));
    setProviderFilters((current) =>
      current.every((key) => available.has(key))
        ? current
        : current.filter((key) => available.has(key))
    );
  }, [providerFacets, loading, loadFailed]);

  const orderScope = JSON.stringify([
    period,
    root,
    discovery,
    priceGaps,
    sortMode,
    search,
    categoryFilter,
    groupFilter,
    providerFilters,
  ]);
  const visibleModels = useMemo(() => {
    const categorized = metadata.pagination_v113 ? [...presentedModels] : presentedModels.filter((item) =>
      matchesCatalogFiltersV10(item, {
        search,
        category: categoryFilter,
        group: groupFilter,
        providers: providerFilters,
      })
    );
    if (metadata.pagination_v113 && !catalogMetricSortV113(effectiveSort))
      return categorized;
    categorized.sort((left, right) => {
      if (effectiveSort === 'newest')
        return compareCatalogNewestV1021(left, right);
      if (effectiveSort === 'oldest')
        return compareCatalogDateV113(left, right, true);
      if (effectiveSort === 'provider')
        return compareCatalogProviderV113(left, right);
      if (effectiveSort === 'capability')
        return compareCatalogCapabilityV113(left, right);
      if (effectiveSort === 'name') return compareCatalogNameV113(left, right);
      if (discovery || priceGaps)
        return left.model_name.localeCompare(right.model_name);
      if (sortMode === 'success') {
        const healthDifference =
          (catalogHealthScoreV101(right) ?? right.success_rate ?? 0) -
          (catalogHealthScoreV101(left) ?? left.success_rate ?? 0);
        if (healthDifference) return healthDifference;
        if (left.stability_score !== right.stability_score) {
          return right.stability_score - left.stability_score;
        }
        return right.success_rate - left.success_rate;
      }
      if (sortMode === 'latency') {
        const leftLatency = left.average_elapsed_time || Number.MAX_VALUE;
        const rightLatency = right.average_elapsed_time || Number.MAX_VALUE;
        if (leftLatency !== rightLatency) {
          return leftLatency - rightLatency;
        }
      }
      if (sortMode === 'name') {
        return left.model_name.localeCompare(right.model_name);
      }
      if (left.usage_share !== right.usage_share) {
        return right.usage_share - left.usage_share;
      }
      return left.model_name.localeCompare(right.model_name);
    });
    if (
      ['newest', 'oldest', 'name', 'provider', 'capability'].includes(
        effectiveSort
      )
    )
      return categorized;
    return stableCatalogOrderV1015(
      categorized,
      catalogOrder.current.scope === orderScope
        ? catalogOrder.current.names
        : []
    );
  }, [
    categoryFilter,
    groupFilter,
    providerFilters,
    presentedModels,
    search,
    sortMode,
    effectiveSort,
    discovery,
    priceGaps,
    orderScope,
    metadata.pagination_v113,
  ]);

  useLayoutEffect(() => {
    catalogOrder.current = {
      scope: orderScope,
      names: visibleModels.map((item) => item.model_name),
    };
    const anchor = pendingAnchor.current;
    pendingAnchor.current = null;
    restoreCatalogAnchorV1015(catalogElement.current, anchor);
  }, [visibleModels, orderScope, refreshFailed]);

  useEffect(() => {
    if (loading || loadFailed) return;
    const byName = new Map(
      presentedModels.map((item) => [item.model_name, item])
    );
    const names = new Set(byName.keys());
    setInfoModel((item) =>
      item && names.has(item.model_name)
        ? {
            ...byName.get(item.model_name),
            selectedPriceV108: item.selectedPriceV108,
          }
        : null
    );
    setSelectedModel((item) =>
      item && names.has(item.model_name) ? byName.get(item.model_name) : null
    );
    setContactModel((name) => (name && !names.has(name) ? null : name));
  }, [presentedModels, loading, loadFailed]);

  const periodOptions = [
    { key: 'day', value: 'day', text: t('models.period_day') },
    { key: 'month', value: 'month', text: t('models.period_month') },
    { key: 'year', value: 'year', text: t('models.period_year') },
  ];
  const sortOptions = [
    {
      key: 'newest',
      value: 'newest',
      text: displayText.newest,
    },
    { key: 'oldest', value: 'oldest', text: displayText.oldest },
    { key: 'name', value: 'name', text: displayText.name },
    { key: 'provider', value: 'provider', text: displayText.provider },
    { key: 'capability', value: 'capability', text: displayText.capability },
    { key: 'usage', value: 'usage', text: t('models.sort_usage') },
    {
      key: 'success',
      value: 'success',
      text: models.some((item) => item.current_health)
        ? healthText.sort
        : t('models.sort_success'),
    },
    { key: 'latency', value: 'latency', text: t('models.sort_latency') },
  ];

  return (
    <div
      ref={catalogElement}
      className={`dashboard-container management-container-v4 model-page-v72 model-page-v814 model-page-v10${
        discovery ? ' model-discovery-view-v109' : ''
      }`}
    >
      <section className='model-catalog-shell-v814'>
        <header className='model-catalog-header-v814'>
          <div>
            <h1 className='model-catalog-title-v814'>
              {priceGaps
                ? priceGapText.title
                : discovery
                ? discoveryText.title
                : metadata.portal_profile === 'vertex_only'
                ? organizationUsageTextV822(t, 'model_title')
                : t('models.title')}
            </h1>
            {(discovery ||
              admin ||
              metadata.portal_profile === 'vertex_only') && (
              <div className='model-catalog-description-v814'>
                {priceGaps
                  ? priceGapText.scope
                  : discovery
                  ? discoveryText.description
                  : admin
                  ? t('models.admin_description')
                  : organizationUsageTextV822(t, 'model_description')}
              </div>
            )}
            {discovery && (
              <Button
                className='model-discovery-contact-v109'
                basic
                onClick={() => setContactModel('')}
              >
                <Icon name='comment alternate outline' />
                {discoveryText.contact}
              </Button>
            )}
          </div>
          {!admin && (
            <Button
              className='model-discovery-toggle-v109'
              onClick={switchCatalog}
              aria-pressed={discovery}
            >
              <Icon name={discovery ? 'arrow left' : 'compass outline'} />
              {discovery ? discoveryText.back : discoveryText.explore}
            </Button>
          )}
        </header>

        {!discovery && !priceGaps && (
          <OfficialModelsV115
            checked={officialOnly}
            language={i18n?.language}
            onChange={(_, { checked }) => {
              setOfficialOnly(checked);
              setInfoModel(null);
              setSelectedModel(null);
            }}
          />
        )}

        <div className='model-filter-band-v814'>
          {root && (
            <div className='model-price-gap-filter-v1018'>
              <Checkbox
                id='model-price-gap-toggle-v1018'
                toggle
                checked={priceGaps}
                label={{
                  children: (
                    <span className='model-price-gap-label-v1018'>
                      {priceGapText.toggle}
                    </span>
                  ),
                }}
                onChange={switchPriceGaps}
              />
            </div>
          )}
          <div className='model-filter-primary-v814'>
            <div className='model-category-scroll-v814'>
              <Button.Group
                size='small'
                className='model-category-buttons-v814'
                aria-label={t('models.catalog.category_filter')}
              >
                {CATEGORY_FILTERS_V814.map((filter) => {
                  const active = categoryFilter === filter.key;
                  return (
                    <Button
                      key={filter.key}
                      active={active}
                      basic={!active}
                      color={active ? 'blue' : undefined}
                      onClick={() => setCategoryFilter(filter.key)}
                      aria-pressed={active}
                    >
                      <Icon name={filter.icon} />
                      {t(`models.catalog.category_${filter.key}`)}
                    </Button>
                  );
                })}
              </Button.Group>
            </div>
            {!discovery && !priceGaps && (
              <Dropdown
                className='model-group-filter-v814'
                fluid
                search
                selection
                value={groupFilter}
                options={groupOptions}
                placeholder={t('models.catalog.group_all')}
                aria-label={t('models.catalog.group_filter')}
                onChange={(_, data) => setGroupFilter(data.value)}
              />
            )}
          </div>

          <div className='model-filter-secondary-v814'>
            <Input
              fluid
              icon='search'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('models.catalog_v10.search')}
              aria-label={t('models.catalog_v10.search')}
            />
            <Dropdown
              fluid
              selection
              value={effectiveSort}
              options={
                discovery || priceGaps || compactMode
                  ? sortOptions.filter((item) =>
                      [
                        'newest',
                        'oldest',
                        'name',
                        'provider',
                        'capability',
                      ].includes(item.value)
                    )
                  : sortOptions.map((item) => ({
                      ...item,
                      text: item.text + (metadata.pagination_v113 && catalogMetricSortV113(item.value) ? paginationText.current : ''),
                    }))
              }
              aria-label={t('models.catalog_v10.sort')}
              onChange={(_, data) => changeSort(data.value)}
            />
            {root && !priceGaps && !compactMode && (
              <Dropdown
                fluid
                selection
                value={period}
                options={periodOptions}
                aria-label={t('models.period')}
                onChange={(_, data) => changePeriod(data.value)}
              />
            )}
          </div>
          {!discovery && !priceGaps && (
            <div className='model-display-controls-v113'>
              <Checkbox
                id='model-compact-v113'
                toggle
                checked={compactMode}
                label={paginationText.compact}
                onChange={changeCompact}
              />
              <Checkbox
                id='model-full-precision-v113'
                toggle
                checked={fullPrecision}
                label={displayText.precision}
                onChange={(_, { checked }) => {
                  setFullPrecision(checked);
                  try {
                    localStorage.setItem(
                      MODEL_PRECISION_KEY_V113,
                      String(checked)
                    );
                  } catch {
                    // The current view still works when browser storage is blocked.
                  }
                }}
              />
            </div>
          )}
          {!loading && !loadFailed && providerFacets.length > 0 && (
            <div className='model-provider-filter-v10'>
              <span
                className='model-provider-label-v10'
                id='model-provider-filter-label'
              >
                {t('models.catalog_v10.providers')}
              </span>
              <div
                className='model-provider-options-v10'
                role='group'
                aria-labelledby='model-provider-filter-label'
              >
                <button
                  type='button'
                  className='model-provider-chip-v10'
                  aria-pressed={providerFilters.length === 0}
                  onClick={() => setProviderFilters([])}
                >
                  {t('models.catalog_v10.all_providers')}
                  <span className='model-provider-count-v10' aria-hidden='true'>
                    {facetTotal}
                  </span>
                </button>
                {providerFacets.map(({ key, brand, count }) => (
                  <button
                    type='button'
                    key={key}
                    className='model-provider-chip-v10'
                    data-provider={key}
                    aria-label={
                      key === 'custom'
                        ? t('models.catalog_v10.other')
                        : brand.name
                    }
                    aria-pressed={providerFilters.includes(key)}
                    disabled={count === 0 && !providerFilters.includes(key)}
                    onClick={() => toggleProvider(key)}
                  >
                    <ModelBrandIconV10 brand={brand} small />
                    {key === 'custom'
                      ? t('models.catalog_v10.other')
                      : brand.name}
                    <span
                      className='model-provider-count-v10'
                      aria-hidden='true'
                    >
                      {count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {!loading && !loadFailed && (
          <div className='model-results-meta-v814'>
            <span role='status' aria-live='polite'>
              {refreshFailed
                ? t('models.background_refresh_failed_v1015', {
                    defaultValue: i18n?.language?.startsWith('zh')
                      ? '\u66f4\u65b0\u5931\u8d25\uff0c\u5f53\u524d\u663e\u793a\u4e0a\u6b21\u6570\u636e'
                      : 'Update failed. Showing last loaded data.',
                  })
                : t('models.catalog.count_filtered', {
                    visible: visibleModels.length,
                    total: metadata.pagination_v113?.total ?? models.length,
                  })}
            </span>
            {!discovery && !priceGaps && (
              <span className='model-catalog-currency-v108'>
                {t('models.catalog_v10.price_currency')}
              </span>
            )}
            {models.some(
              (item) =>
                item.prices_state_v1020 === 'error' ||
                item.statistics_state_v1020 === 'error'
            ) && (
              <Button
                basic
                compact
                icon
                aria-label={t('models.retry')}
                title={t('models.retry')}
                onClick={() => loadModels(true)}
              >
                <Icon name='refresh' />
              </Button>
            )}
            {admin && metadata.generated_at ? (
              <span>
                {t('models.updated_at', {
                  time: formatTimestamp(metadata.generated_at),
                })}
              </span>
            ) : null}
            {hasFilters && (
              <Button
                basic
                compact
                size='small'
                className='model-reset-v10'
                onClick={resetFilters}
              >
                <Icon name='undo' />
                {t('models.catalog_v10.reset')}
              </Button>
            )}
          </div>
        )}
        {!loadFailed && !discovery && !priceGaps && (
          <CatalogPaginationV113 pagination={metadata.pagination_v113}
            text={paginationText} disabled={loading} onChange={changePage} />
        )}
        {loading ? (
          <Segment basic style={{ minHeight: '180px' }}>
            <Loader
              active
              inline='centered'
              content={discovery ? discoveryText.loading : t('models.loading')}
            />
          </Segment>
        ) : loadFailed ? (
          <Message negative>
            <Message.Header>
              {discovery ? discoveryText.failed : t('models.load_failed')}
            </Message.Header>
            <Button
              basic
              negative
              size='small'
              onClick={loadModels}
              style={{ marginTop: '12px' }}
            >
              <Icon name='refresh' />
              {t('models.retry')}
            </Button>
          </Message>
        ) : visibleModels.length === 0 ? (
          <Message
            info
            content={
              officialOnly && !search && categoryFilter === 'all' && !groupFilter && !providerFilters.length
                ? (i18n?.language?.startsWith('zh') ? '暂无可用的官方模型' : 'No official models are currently available')
                : hasFilters
                ? t('models.search_empty')
                : priceGaps
                ? priceGapText.empty
                : discovery
                ? discoveryText.empty
                : t('models.empty')
            }
          />
        ) : (
          <>
            {admin &&
              !compactMode &&
              !priceGaps &&
              (metadata.catalog_stage_v1020 !== 'summary' ||
                metadata.statistics_ready_v1020) && (
                <Message
                  info
                  content={t('models.platform_total_requests', {
                    count: formatNumber(metadata.platform_total_requests),
                  })}
                />
              )}
            <Card.Group className='model-cards-v814'>
              {visibleModels.map((item) =>
                discovery ? (
                  <OtherModelCardV109
                    key={item.model_name}
                    item={item}
                    text={discoveryText}
                    onContact={setContactModel}
                  />
                ) : (
                  <ModelCard
                    key={item.model_name}
                    item={item}
                    admin={admin}
                    root={root}
                    compactMode={compactMode}
                    officialOnly={officialOnly && !priceGaps}
                    t={t}
                    healthText={healthText}
                    language={i18n?.language}
                    fullPrecision={fullPrecision}
                    sortMode={effectiveSort}
                    priceGapText={priceGaps ? priceGapText : null}
                    onCopy={copyModelId}
                    onDetails={setSelectedModel}
                    onInfo={setInfoModel}
                  />
                )
              )}
            </Card.Group>
          </>
        )}
        {!loading && !loadFailed && !discovery && !priceGaps && (
          <CatalogPaginationV113 pagination={metadata.pagination_v113}
            text={paginationText} disabled={false} onChange={changePage} />
        )}
      </section>

      <ModelContactV109
        model={contactModel}
        contact={metadata.contact}
        text={discoveryText}
        closeLabel={t('models.close')}
        onClose={() => setContactModel(null)}
      />

      <Modal
        open={Boolean(infoModel)}
        onClose={() => setInfoModel(null)}
        size='small'
        className='model-info-modal-v10'
        aria-label={t('models.catalog_v10.details')}
      >
        <Modal.Header>{t('models.catalog_v10.details')}</Modal.Header>
        <Modal.Content scrolling>
          {infoModel && (
            <>
              <ModelHoverDetailsV814
                item={infoModel}
                root={root}
                presentation={infoModel.presentation}
                t={t}
                language={i18n?.language}
                fullPrecision={fullPrecision}
              />
              {admin && infoModel.admin?.error_codes?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <strong>{t('models.frequent_errors')}</strong>
                  {infoModel.admin.error_codes
                    .filter((error) => Number(error.share) > 25)
                    .map((error) => (
                      <div key={`${error.error_code}-${error.http_status}`}>
                        {error.error_code} / {error.http_status || '-'} /{' '}
                        {formatPercent(error.share)}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setInfoModel(null)}>
            {t('models.close')}
          </Button>
        </Modal.Actions>
      </Modal>
      {admin && (
        <ModelStatisticsModal
          item={selectedModel}
          period={period}
          bucketUnit={metadata.bucket_unit}
          t={t}
          language={i18n?.language}
          onClose={() => setSelectedModel(null)}
        />
      )}
    </div>
  );
};

const ModelCard = ({
  item,
  admin,
  root,
  compactMode,
  officialOnly = false,
  t,
  onCopy,
  onDetails,
  onInfo,
  healthText,
  language,
  priceGapText,
  fullPrecision,
  sortMode,
}) => {
  const hasData = item.has_data;
  const presentation = item.presentation || buildModelPresentationV10(item);
  const [selectedPrice, setSelectedPrice] = useState('');
  const priceOptions = catalogPriceOptionsV108(
    presentation,
    t('models.catalog_v10.base_source')
  );
  const activePrice =
    priceOptions.find((quote) => quote.id === selectedPrice) || priceOptions[0];
  const displayName = item.display_name || item.model_name;
  const hasDistinctDisplayName = displayName !== item.model_name;
  const hasConfiguredPrice = Boolean(activePrice);
  return (
    <Card
      className={`model-card-v814 ${officialOnly ? 'model-card-official-v115' : ''} ${compactMode ? 'model-card-compact-v113' : ''} ${
        hasConfiguredPrice
          ? 'model-card-priced-v814'
          : 'model-card-unpriced-v814'
      }`}
      data-model={item.model_name}
      data-provider={presentation.providerKey}
    >
      {officialOnly && <OfficialSealV115 className='model-official-watermark-v115' />}
      <Card.Content>
        <div className='model-identity-v814'>
          {officialOnly ? (
            <div className='model-official-brand-v115'>
              <ModelBrandIconV10 brand={presentation.brand} />
              <OfficialShieldV115 className='model-official-brand-mark-v115' />
            </div>
          ) : <ModelBrandIconV10 brand={presentation.brand} />}
          <div>
            {hasDistinctDisplayName ? (
              <div className='model-display-name-v814' title={displayName}>
                {displayName}
              </div>
            ) : null}
            <button
              type='button'
              className={`model-id-button-v814 ${
                hasDistinctDisplayName ? '' : 'model-id-primary-v814'
              }`}
              title={item.model_name}
              onClick={() => onCopy(item.model_name)}
            >
              {item.model_name}
            </button>
            {!hasDistinctDisplayName && (
              <span className='model-brand-name-v10'>
                {presentation.providerKey === 'custom'
                  ? t('models.catalog_v10.other')
                  : presentation.brand.name}
              </span>
            )}
          </div>
          <Button
            basic
            compact
            icon
            className='model-copy-button-v814'
            aria-label={t('models.copy_model')}
            title={t('models.copy_model')}
            onClick={() => onCopy(item.model_name)}
          >
            <Icon name='copy outline' />
          </Button>
        </div>
        <CatalogModelMetadataV113
          item={item}
          language={language}
          sortMode={sortMode}
        />
      </Card.Content>
      <Card.Content className='model-card-body-v10'>
        {!priceGapText && (
          <ModelSiteOriginsV1024 item={item} root={root} language={language} />
        )}
        {priceGapText ? (
          <div className='model-price-gap-state-v1018'>
            <Label basic color='orange'>
              {item.base_price_state_v1018 === 'scheduled'
                ? priceGapText.scheduled
                : priceGapText.missing}
            </Label>
            {item.next_effective_at_v1018 > 0 && (
              <span>
                {priceGapText.next}:{' '}
                {formatTimestamp(item.next_effective_at_v1018)}
              </span>
            )}
            <RegisteredChannelsV1021
              item={item}
              root={root}
              language={language}
            />
            {!item.enabled_channel_count_v1018 && (
              <span>{priceGapText.offline}</span>
            )}
          </div>
        ) : item.prices_state_v1020 && item.prices_state_v1020 !== 'ready' ? (
          <div className='model-price-unconfigured-v814' role='status'>
            <Icon
              name={
                item.prices_state_v1020 === 'pending'
                  ? 'clock outline'
                  : 'warning circle'
              }
            />
            {catalogStageTextV1020(language, 'prices', item.prices_state_v1020)}
          </div>
        ) : (
          <ModelPriceBoxV814
            presentation={presentation}
            options={priceOptions}
            active={activePrice}
            onChange={setSelectedPrice}
            t={t}
            fullPrecision={fullPrecision}
          />
        )}
        {!priceGapText && !compactMode && (
          <div className='model-card-statistics-v108'>
            <Card.Meta className='model-status-v814'>
              {item.statistics_state_v1020 &&
              item.statistics_state_v1020 !== 'ready' ? (
                <Label basic>
                  {catalogStageTextV1020(
                    language,
                    'statistics',
                    item.statistics_state_v1020
                  )}
                </Label>
              ) : !hasData ? (
                <Label basic>{t('models.no_statistics')}</Label>
              ) : !item.has_sufficient_sample ? (
                <Label basic color='orange'>
                  {t('models.low_sample')}
                </Label>
              ) : (
                <Label basic color='green'>
                  {t('models.global_statistics')}
                </Label>
              )}
            </Card.Meta>
            <dl className='model-metrics-v10'>
              <div>
                <dt
                  title={
                    item.current_health?.observed === false
                      ? healthText.unobserved
                      : healthText.hint
                  }
                >
                  {item.current_health
                    ? healthText.label
                    : t('models.success_rate')}
                </dt>
                <dd
                  title={
                    item.current_health?.observed === false
                      ? healthText.unobserved
                      : `${healthText.hint} ${healthText.historical}: ${
                          hasData ? formatPercent(item.success_rate) : '-'
                        }`
                  }
                >
                  {catalogHealthScoreV101(item) !== null
                    ? formatPercent(catalogHealthScoreV101(item))
                    : hasData
                    ? formatPercent(item.success_rate)
                    : '-'}
                </dd>
              </div>
              <div>
                <dt title={t('models.usage_share')}>
                  {t('models.usage_share')}
                </dt>
                <dd>{hasData ? formatPercent(item.usage_share) : '-'}</dd>
              </div>
              <div>
                <dt title={t('models.average_latency')}>
                  {t('models.average_latency')}
                </dt>
                <dd>{formatMilliseconds(item.average_elapsed_time)}</dd>
              </div>
              <div>
                <dt title={t('models.average_ttft')}>
                  {t('models.average_ttft')}
                </dt>
                <dd>{formatMilliseconds(item.average_first_response_time)}</dd>
              </div>
            </dl>
          </div>
        )}
      </Card.Content>
      <Card.Content extra>
        <div className='model-footer-summary-v108'>
          <ModelCategoryLabelsV814 presentation={presentation} t={t} />
          {admin && !compactMode && item.admin && (
            <span className='model-footer-meta-v10'>
              {`${formatNumber(item.admin.total_requests)} ${t(
                'models.catalog_v10.requests'
              )}`}
            </span>
          )}
        </div>
        <div className='model-card-tools-v10'>
          <ModelToolV10
            icon='info circle'
            label={t('models.catalog_v10.details')}
            onClick={() =>
              onInfo({ ...item, selectedPriceV108: activePrice?.id })
            }
          />
          {admin && !compactMode && !priceGapText && (
            <ModelToolV10
              icon='chart line'
              label={t('models.view_details')}
              disabled={
                item.statistics_state_v1020 &&
                item.statistics_state_v1020 !== 'ready'
              }
              onClick={() => onDetails(item)}
            />
          )}
          {root && (
            <ModelToolV10
              icon='dollar sign'
              label={priceGapText ? priceGapText.base : t('header.basePrices')}
              href={`/base-prices?model=${encodeURIComponent(item.model_name)}`}
            />
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

const ModelToolV10 = ({ icon, label, onClick, href, disabled }) => (
  <Popup
    content={label}
    on={['hover']}
    position='top center'
    trigger={
      <Button
        {...(href ? { as: 'a', href } : {})}
        basic
        icon
        size='small'
        aria-label={label}
        title={label}
        onClick={onClick}
        disabled={disabled}
      >
        <Icon name={icon} />
      </Button>
    }
  />
);

const ModelCategoryLabelsV814 = ({ presentation, t }) => (
  <div className='model-tags-v814'>
    {(presentation?.categories || []).map((category) => {
      const metadata = MODEL_CATEGORY_META_V814[category];
      if (!metadata) return null;
      return (
        <Label basic size='mini' key={category}>
          <Icon name={metadata.icon} />
          {t(metadata.labelKey)}
        </Label>
      );
    })}
    {presentation?.thinking ? (
      <Label basic color='violet' size='mini'>
        <Icon name='lightbulb outline' />
        {t('models.catalog.thinking')}
      </Label>
    ) : null}
  </div>
);

const ModelPriceBoxV814 = ({
  presentation,
  options,
  active,
  onChange,
  t,
  fullPrecision,
}) => {
  if (!active) {
    return (
      <div className='model-price-unconfigured-v814'>
        <Icon name='dollar sign' />
        <span>
          {t(
            presentation?.sourceDirectoryV107
              ? 'models.catalog.source_unavailable'
              : 'models.catalog.price_unconfigured'
          )}
        </span>
      </div>
    );
  }
  return (
    <div className='model-price-box-v814'>
      <div className='model-price-source-header-v107'>
        <SourcePriceSelectorV107
          quotes={options}
          selected={active.id}
          onChange={onChange}
          formatMultiplier={(value) => catalogDecimalV113(value, fullPrecision)}
        />
      </div>
      <ModelCardPriceSummaryV108
        prices={active.customer_prices}
        categories={presentation.categories}
        t={t}
        fullPrecision={fullPrecision}
      />
    </div>
  );
};

const ModelCardPriceSummaryV108 = ({
  prices,
  categories,
  t,
  fullPrecision,
}) => {
  const rows = selectCardPricesV108(prices, categories);
  return (
    <div className='model-card-price-summary-v108'>
      {rows.map((price) => (
        <div
          className='model-card-price-item-v108'
          key={price.summaryKeyV111 || `${price.meter}:${price.unit}`}
        >
          <span className='model-card-price-label-v108'>
            {priceSummaryLabelV108(price, t)}
          </span>
          <span
            className='model-card-price-value-v108'
            title={`${catalogPriceV113(price.unit_amount_usd, fullPrecision)} ${
              isMediaPriceV111(price)
                ? mediaPriceUnitV111(price, t)
                : translateUnitV814(t, price.unit)
            }`}
          >
            {catalogPriceV113(price.unit_amount_usd, fullPrecision)}
            {price.from && (
              <small className='model-card-price-from-v108'>
                {t('models.catalog_v10.from')}
              </small>
            )}
            <span className='model-price-unit-v10'>
              {isMediaPriceV111(price)
                ? mediaPriceUnitV111(price, t)
                : t(`models.catalog_v10.units.${price.unit}`, {
                    defaultValue: price.unit ? `/${price.unit}` : '',
                  })}
            </span>
          </span>
          {isMediaPriceV111(price) && (
            <span
              className='model-card-price-scope-v111'
              title={mediaPriceScopeV111(price.attributes, t, true)}
            >
              {mediaPriceScopeV111(price.attributes, t, true)}
            </span>
          )}
        </div>
      ))}
      {rows.some(isMediaPriceV111) && (
        <span className='model-card-price-note-v111'>
          {t('models.catalog_v10.media_price.component_rates', {
            shown: rows.length,
            total: prices.filter(isMediaPriceV111).length,
          })}
        </span>
      )}
      {!rows.length && <span>{t('models.catalog_v10.details')}</span>}
    </div>
  );
};

const ModelHoverDetailsV814 = ({
  item,
  presentation,
  t,
  language,
  root,
  fullPrecision,
}) => {
  const [selectedSource, setSelectedSource] = useState(
    item.selectedPriceV108 || ''
  );
  const sourceOptions = catalogPriceOptionsV108(
    presentation,
    t('models.catalog_v10.base_source')
  );
  const source =
    sourceOptions.find((quote) => quote.id === selectedSource) ||
    sourceOptions[0];
  const provider = presentation?.provider || MODEL_BRANDS_V10.custom;
  const prices = source?.customer_prices || [];
  return (
    <div>
      <div className='model-popup-heading-v814'>
        <ModelBrandIconV10 brand={presentation?.brand || provider} />
        <div>
          <strong>{item.display_name || item.model_name}</strong>
          <div className='model-popup-subtitle-v814'>
            {provider.name} / {item.model_name}
          </div>
        </div>
      </div>
      <ModelCategoryLabelsV814 presentation={presentation} t={t} />
      <CatalogModelMetadataV113 item={item} language={language} details />
      <ModelSiteOriginsV1024
        item={item}
        root={root}
        language={language}
        inline
      />
      {sourceOptions.length > 0 && (
        <SourcePriceSelectorV107
          quotes={sourceOptions}
          selected={selectedSource}
          onChange={setSelectedSource}
          formatMultiplier={(value) => catalogDecimalV113(value, fullPrecision)}
        />
      )}
      {source?.price_is_estimate && (
        <p className='model-popup-subtitle-v814'>
          {t('models.catalog_v10.price_estimate')}
        </p>
      )}
      <div className='model-popup-groups-v814'>
        <strong>{t('models.catalog.groups')}:</strong>
        {(Array.isArray(item.groups) ? item.groups : []).map((group) => (
          <Label basic size='mini' key={group}>
            {group}
          </Label>
        ))}
      </div>
      {prices.some(isMediaPriceV111) && (
        <p className='model-popup-subtitle-v814'>
          {t('models.catalog_v10.media_price.additive')}
        </p>
      )}
      {item.prices_state_v1020 && item.prices_state_v1020 !== 'ready' ? (
        <Message
          info
          size='small'
          content={catalogStageTextV1020(
            language,
            'prices',
            item.prices_state_v1020
          )}
        />
      ) : prices.length === 0 ? (
        <Message
          info
          size='small'
          content={t(
            presentation?.sourceDirectoryV107
              ? 'models.catalog.source_unavailable'
              : 'models.catalog.price_unconfigured'
          )}
        />
      ) : (
        <div className='model-popup-price-table-v814'>
          <Table basic='very' compact unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('models.commercial.meter')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('models.commercial.price')}
                </Table.HeaderCell>
                <Table.HeaderCell>{t('models.catalog.scope')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {prices.map((price, index) => (
                <Table.Row
                  key={`${price.meter}-${JSON.stringify(
                    price.attributes || {}
                  )}-${index}`}
                >
                  <Table.Cell>
                    <strong>
                      {isMediaPriceV111(price)
                        ? mediaPriceLabelV111(price, t)
                        : translateMeterV814(t, price.meter)}
                    </strong>
                    <div className='model-popup-subtitle-v814'>
                      {isMediaPriceV111(price)
                        ? mediaPriceUnitV111(price, t)
                        : translateUnitV814(t, price.unit)}
                    </div>
                  </Table.Cell>
                  <Table.Cell textAlign='right'>
                    <span className='model-price-value-v814'>
                      {catalogPriceV113(price.unit_amount_usd, fullPrecision)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className='model-popup-scope-v814'>
                    {formatPriceScopeV814(price.attributes, t)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};

const translateMeterV814 = (t, meter) =>
  t(`models.commercial.meters.${meter}`, { defaultValue: meter });

const translateUnitV814 = (t, unit) =>
  t(`models.commercial.units.${unit}`, { defaultValue: unit });

const CatalogModelMetadataV113 = ({
  item,
  language,
  sortMode,
  details = false,
}) => {
  const text = catalogDisplayTextV113(language);
  const benchmark = catalogIntelligenceV113(item);
  const date = catalogDateV113(item);
  const stamp = (value) => new Date(value * 1000).toISOString().slice(0, 10);
  if (!details && !['newest', 'oldest', 'capability'].includes(sortMode))
    return null;
  return (
    <div className='model-metadata-v113'>
      {(details || ['newest', 'oldest'].includes(sortMode)) && (
        <span>
          {text[date.source]}
          {date.timestamp ? `: ${stamp(date.timestamp)}` : ''}
        </span>
      )}
      {(details || sortMode === 'capability') && (
        <span
          title={
            benchmark
              ? `${text.source} · ${text.updated}: ${stamp(
                  benchmark.updated_at
                )}`
              : undefined
          }
        >
          {benchmark
            ? `${text.intelligence}: ${benchmark.intelligence_index}${
                benchmark.stale ? ` (${text.stale})` : ''
              }`
            : `${text.intelligence}: ${text.unknown}`}
        </span>
      )}
      {details && benchmark && (
        <span>
          {text.source} · {text.updated}: {stamp(benchmark.updated_at)}
        </span>
      )}
    </div>
  );
};

const formatPriceScopeV814 = (attributes, t) => {
  const scope = attributes && typeof attributes === 'object' ? attributes : {};
  if (scope.quantity && scope.group && scope.media_kind)
    return mediaPriceScopeV111(scope, t, true);
  const pieces = [];
  if (scope.ttl_minutes) {
    pieces.push(
      t('models.commercial.cache_ttl.duration', { minutes: scope.ttl_minutes })
    );
  }
  const minimum = Number(scope.context_min_tokens);
  const maximum = Number(scope.context_max_tokens);
  const hasMinimum = Number.isFinite(minimum) && minimum > 0;
  const hasMaximum = Number.isFinite(maximum) && maximum > 0;
  if (hasMinimum && hasMaximum) {
    pieces.push(
      t('models.catalog.context_range', {
        minimum: formatNumber(minimum),
        maximum: formatNumber(maximum),
      })
    );
  } else if (hasMinimum) {
    pieces.push(
      t('models.catalog.context_from', { count: formatNumber(minimum) })
    );
  } else if (hasMaximum) {
    pieces.push(
      t('models.catalog.context_up_to', { count: formatNumber(maximum) })
    );
  } else {
    pieces.push(t('models.catalog.base_price'));
  }
  if (scope.service_tier) pieces.push(String(scope.service_tier).toUpperCase());
  if (scope.period_value && scope.period_unit) {
    pieces.push(
      `${t('models.commercial.cache_storage.period_value')}: ${
        scope.period_value
      } ${t(
        `models.commercial.cache_storage.period_units.${scope.period_unit}`
      )}`
    );
  }
  if (scope.rounding) {
    pieces.push(
      t(`models.commercial.cache_storage.rounding_options.${scope.rounding}`, {
        defaultValue: scope.rounding,
      })
    );
  }
  ['size', 'quality', 'resolution', 'aspect_ratio'].forEach((key) => {
    if (scope[key])
      pieces.push(`${t(`models.commercial.attributes.${key}`)}: ${scope[key]}`);
  });
  return pieces.join(' / ');
};

const ModelStatisticsModal = ({
  item,
  period,
  bucketUnit,
  t,
  language,
  onClose,
}) => {
  const admin = item && item.admin;
  const frequentErrors =
    admin && Array.isArray(admin.error_codes)
      ? admin.error_codes.filter((error) => Number(error.share) > 25)
      : [];
  const trend = useMemo(() => {
    if (!admin || !Array.isArray(admin.trend)) return [];
    return admin.trend.map((point) => ({
      ...point,
      label: formatBucket(point.bucket_start, bucketUnit),
    }));
  }, [admin, bucketUnit]);

  return (
    <Modal open={!!item} onClose={onClose} size='large'>
      <Modal.Header>{item ? item.model_name : ''}</Modal.Header>
      <Modal.Content scrolling>
        {item?.statistics_state_v1020 &&
        item.statistics_state_v1020 !== 'ready' ? (
          <Message
            info
            content={catalogStageTextV1020(
              language,
              'statistics',
              item.statistics_state_v1020
            )}
          />
        ) : item && admin ? (
          <>
            <Statistic.Group widths='four' size='small'>
              <Statistic
                label={t('models.total_requests')}
                value={formatNumber(admin.total_requests)}
              />
              <Statistic
                label={t('models.success_count')}
                value={formatNumber(admin.success_count)}
              />
              <Statistic
                label={t('models.failure_count')}
                value={formatNumber(admin.failure_count)}
              />
              <Statistic
                label={t('models.total_tokens')}
                value={formatNumber(admin.total_tokens)}
              />
            </Statistic.Group>

            {trend.length > 0 ? (
              <div
                className='model-detail-chart-v72'
                style={{ width: '100%', height: '320px', marginTop: '24px' }}
              >
                <ResponsiveContainer>
                  <ComposedChart data={trend}>
                    <CartesianGrid strokeDasharray='3 3' opacity={0.25} />
                    <XAxis dataKey='label' minTickGap={20} />
                    <YAxis yAxisId='requests' allowDecimals={false} />
                    <YAxis
                      yAxisId='rate'
                      orientation='right'
                      domain={[0, 100]}
                      unit='%'
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      yAxisId='requests'
                      dataKey='total_requests'
                      name={t('models.total_requests')}
                      fill='#2185d0'
                    />
                    <Line
                      yAxisId='rate'
                      type='monotone'
                      dataKey='success_rate'
                      name={t('models.success_rate')}
                      stroke='#21ba45'
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Message info content={t('models.no_statistics')} />
            )}

            {frequentErrors.length > 0 ? (
              <Table compact unstackable style={{ marginTop: '24px' }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>
                      {t('models.error_code')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>HTTP</Table.HeaderCell>
                    <Table.HeaderCell textAlign='right'>
                      {t('models.failure_count')}
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign='right'>
                      {t('models.error_share')}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {frequentErrors.map((error) => (
                    <Table.Row key={`${error.error_code}-${error.http_status}`}>
                      <Table.Cell>{error.error_code}</Table.Cell>
                      <Table.Cell>{error.http_status || '-'}</Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatNumber(error.count)}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatPercent(error.share)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : null}

            <Table definition compact stackable style={{ marginTop: '24px' }}>
              <Table.Body>
                <DetailRow
                  label={t('models.period')}
                  value={t(`models.period_${period}`)}
                />
                <DetailRow
                  label={t('models.success_rate')}
                  value={formatPercent(item.success_rate)}
                />
                <DetailRow
                  label={t('models.stability_score')}
                  value={formatPercent(item.stability_score)}
                />
                <DetailRow
                  label={t('models.usage_share')}
                  value={formatPercent(item.usage_share)}
                />
                <DetailRow
                  label={t('models.prompt_tokens')}
                  value={formatNumber(admin.prompt_tokens)}
                />
                <DetailRow
                  label={t('models.completion_tokens')}
                  value={formatNumber(admin.completion_tokens)}
                />
                <DetailRow
                  label={t('models.quota')}
                  value={formatNumber(admin.quota)}
                />
                <DetailRow
                  label={t('models.average_latency')}
                  value={formatMilliseconds(item.average_elapsed_time)}
                />
                <DetailRow
                  label={t('models.average_ttft')}
                  value={formatMilliseconds(item.average_first_response_time)}
                />
                <DetailRow
                  label={t('models.stream_rate')}
                  value={formatPercent(item.stream_rate)}
                />
                <DetailRow
                  label={t('models.last_success')}
                  value={formatTimestamp(admin.last_success_at)}
                />
                <DetailRow
                  label={t('models.last_failure')}
                  value={formatTimestamp(admin.last_failure_at)}
                />
              </Table.Body>
            </Table>
          </>
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('models.close')}</Button>
      </Modal.Actions>
    </Modal>
  );
};

const DetailRow = ({ label, value }) => (
  <Table.Row>
    <Table.Cell width={5}>{label}</Table.Cell>
    <Table.Cell>{value}</Table.Cell>
  </Table.Row>
);

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatMilliseconds = (value) =>
  value > 0 ? `${Number(value).toFixed(0)} ms` : '-';
const formatTimestamp = (value) =>
  value > 0 ? new Date(value * 1000).toLocaleString() : '-';
const formatBucket = (value, unit) => {
  const date = new Date(value * 1000);
  if (unit === 'hour') {
    return date.toLocaleString([], {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
    });
  }
  if (unit === 'month') {
    return date.toLocaleDateString([], { year: 'numeric', month: 'short' });
  }
  return date.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
};

export default Model;
